import { Button, Checkbox, Input, List, Modal, QRCode, Tooltip } from 'ant-design-vue';
import { ref, h } from 'vue';
import { Select, message } from 'ant-design-vue';
import { FirebaseError, getApp } from 'firebase/app';
import type {
  UserCredential,
  MultiFactorError,
  PhoneMultiFactorInfo,
  MultiFactorInfo,
  MultiFactorResolver,
} from 'firebase/auth';
import type { Router } from 'vue-router';
import { hostname } from '../../assets/js/helpers/DomainUtils.ts';
import {
  getAuth,
  getMultiFactorResolver,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  PhoneAuthProvider,
  multiFactor,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';

function isPhoneMultiFactorInfo(hint: MultiFactorInfo): hint is PhoneMultiFactorInfo {
  return hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID;
}

function clearRecaptchaVerifier() {
  try {
    window.recaptchaVerifier?.clear?.();
  } catch {
    // Firebase may already have cleared the verifier.
  }
}

function setModalError(id: string, text: string) {
  const errorEl = document.querySelector<HTMLElement>(`#${id}`);
  if (!errorEl) return;
  errorEl.textContent = text;
  errorEl.classList.remove('hidden');
}

function clearModalError(id: string) {
  const errorEl = document.querySelector<HTMLElement>(`#${id}`);
  if (!errorEl) return;
  errorEl.textContent = '';
  errorEl.classList.add('hidden');
}

function canUseSmsMfaOnCurrentDomain() {
  return hostname() === import.meta.env.VITE_HOSTNAME || hostname() === 'localhost';
}

export interface InlineMfaMethod {
  uid: string,
  label: string,
  factorId: 'phone' | 'totp',
}

export interface InlineMfaChallenge {
  methods: InlineMfaMethod[],
  selectedMethodUid: string,
  phoneCodeSent: boolean,
  error: string | null,
  loading: boolean,
  showMethodSelector: boolean,
  showRecaptchaDisclaimer: boolean,
}

const inlineMfaChallenge = ref<InlineMfaChallenge | null>(null);
let inlineMfaResolver: MultiFactorResolver | null = null;
let inlineMfaPhoneVerificationId: string | null = null;

function setInlineMfaLoading(loading: boolean) {
  if (!inlineMfaChallenge.value) return;
  inlineMfaChallenge.value = {
    ...inlineMfaChallenge.value,
    loading,
  };
}

function setInlineMfaError(error: string | null) {
  if (!inlineMfaChallenge.value) return;
  inlineMfaChallenge.value = {
    ...inlineMfaChallenge.value,
    error,
  };
}

function getSelectedInlineMfaMethod(): InlineMfaMethod | null {
  if (!inlineMfaChallenge.value) return null;
  return inlineMfaChallenge.value.methods.find(
    (method) => method.uid === inlineMfaChallenge.value?.selectedMethodUid,
  ) ?? null;
}

export function useInlineMfaChallenge() {
  return inlineMfaChallenge;
}

export function clearInlineMfaChallenge() {
  inlineMfaResolver = null;
  inlineMfaPhoneVerificationId = null;
  inlineMfaChallenge.value = null;
  clearRecaptchaVerifier();
}

export function beginInlineMfaChallenge(
  data: MultiFactorError,
): InlineMfaChallenge {
  const auth = getAuth(getApp(this.firebaseApp));
  const resolver = getMultiFactorResolver(auth, data);
  const smsSupported = canUseSmsMfaOnCurrentDomain();
  const hints = resolver.hints.filter((hint) => {
    return smsSupported || hint.factorId !== PhoneMultiFactorGenerator.FACTOR_ID;
  });

  if (hints.length === 0) {
    throw new Error('No supported multi-factor methods are available on this domain.');
  }

  inlineMfaResolver = resolver;
  inlineMfaPhoneVerificationId = null;
  inlineMfaChallenge.value = {
    methods: hints.map((hint) => {
      return isPhoneMultiFactorInfo(hint)
        ? {
          uid: hint.uid,
          label: hint.phoneNumber ?? 'SMS',
          factorId: 'phone',
        }
        : {
          uid: hint.uid,
          label: hint.displayName ?? 'Authenticator App',
          factorId: 'totp',
        };
    }),
    selectedMethodUid: hints[0]?.uid ?? '',
    phoneCodeSent: false,
    error: null,
    loading: false,
    showMethodSelector: hints.length > 1,
    showRecaptchaDisclaimer: hints.some((hint) => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID),
  };

  return inlineMfaChallenge.value;
}

export function selectInlineMfaMethod(methodUid: string) {
  if (!inlineMfaChallenge.value) return;
  inlineMfaPhoneVerificationId = null;
  inlineMfaChallenge.value = {
    ...inlineMfaChallenge.value,
    selectedMethodUid: methodUid,
    phoneCodeSent: false,
    error: null,
  };
}

export async function sendInlineMfaCode(): Promise<void> {
  if (!inlineMfaResolver) {
    throw new Error('MFA session expired. Please try signing in again.');
  }

  const selectedMethod = getSelectedInlineMfaMethod();
  if (!selectedMethod || selectedMethod.factorId !== 'phone') {
    return;
  }

  const multiFactorHint = inlineMfaResolver.hints.find((hint) => hint.uid === selectedMethod.uid);
  if (!multiFactorHint || !isPhoneMultiFactorInfo(multiFactorHint)) {
    throw new Error('Unable to send SMS code.');
  }

  setInlineMfaLoading(true);
  setInlineMfaError(null);

  try {
    const auth = getAuth(getApp(this.firebaseApp));
    const phoneInfoOptions = { multiFactorHint, session: inlineMfaResolver.session };
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    inlineMfaPhoneVerificationId = await phoneAuthProvider.verifyPhoneNumber(
      phoneInfoOptions,
      window.recaptchaVerifier,
    );

    if (!inlineMfaChallenge.value) return;
    inlineMfaChallenge.value = {
      ...inlineMfaChallenge.value,
      phoneCodeSent: true,
      error: null,
      loading: false,
    };
  } catch (error: any) {
    setInlineMfaLoading(false);
    if (error.code === 'auth/invalid-app-credential') {
      setInlineMfaError('SMS 2FA is not enabled for this domain.');
    } else {
      setInlineMfaError('Failed to send SMS code. Please try again.');
    }
    throw error;
  }
}

export async function submitInlineMfaCode(code: string): Promise<UserCredential> {
  if (!inlineMfaResolver || !inlineMfaChallenge.value) {
    throw new Error('MFA session expired. Please try signing in again.');
  }

  const trimmedCode = code.trim();
  if (trimmedCode.length !== 6) {
    setInlineMfaError('Enter the 6-digit code.');
    throw new Error('invalid code');
  }

  const selectedMethod = getSelectedInlineMfaMethod();
  if (!selectedMethod) {
    throw new Error('Select a verification method.');
  }

  setInlineMfaLoading(true);
  setInlineMfaError(null);

  try {
    let result: UserCredential;

    if (selectedMethod.factorId === 'phone') {
      if (!inlineMfaPhoneVerificationId) {
        setInlineMfaLoading(false);
        setInlineMfaError('Send an SMS code before submitting.');
        throw new Error('sms code not sent');
      }

      const credential = PhoneAuthProvider.credential(inlineMfaPhoneVerificationId, trimmedCode);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      result = await inlineMfaResolver.resolveSignIn(assertion);
    } else {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(selectedMethod.uid, trimmedCode);
      result = await inlineMfaResolver.resolveSignIn(assertion);
    }

    clearInlineMfaChallenge();
    return result;
  } catch (error) {
    setInlineMfaLoading(false);
    if (error instanceof FirebaseError && error.code === 'auth/quota-exceeded') {
      setInlineMfaError('Too many attempts. Please try again later.');
      throw error;
    } else if (error instanceof FirebaseError && error.code === 'auth/totp-challenge-timeout') {
      setInlineMfaError('Login session timed out. Please refresh the page and try again.');
      throw error;
    }
    setInlineMfaError('Invalid code. Please try again.');
    throw error;
  }
}

function getCurrentUserEmail() {
  const auth = getAuth(getApp(this.firebaseApp));
  return auth.currentUser?.email
    ?? auth.currentUser?.providerData?.find((provider: { email?: string | null }) => provider?.email)?.email
    ?? '';
}

function formatTotpSecret(secret: string) {
  return secret.match(/.{1,4}/g)?.join(' ') ?? secret;
}

async function enrollTotpFactor({
  requireName,
}: {
  requireName: boolean,
}): Promise<void> {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  if (!auth.currentUser) {
    throw new Error('no user');
  }

  const currentUser = auth.currentUser;
  const multiFactorUser = multiFactor(auth.currentUser);
  const multiFactorSession = await multiFactorUser.getSession();
  const totpSecret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
  const email = getCurrentUserEmail.call(this);

  if (email === '') {
    throw new Error('missing email');
  }

  const totpUri = totpSecret.generateQrCodeUrl(email, 'Rack Manage');
  const secret = totpSecret.secretKey;
  const formattedSecret = formatTotpSecret(secret);
  const confirmedSecretSaved = ref(false);

  await new Promise<void>((resolve, reject) => {
    const modal = Modal.confirm({
      title: 'Enroll TOTP',
      okButtonProps: {
        disabled: true,
      },
      content: h(
        'div',
        {},
        { default: () => [
          h('p', {}, {
            default: () => 'Please scan the QR code or enter the following code in your authenticator app:',
          }),
          h('div', { class: 'ml-6 mt-4' }, [
            h(QRCode, { value: totpUri, size: 256, errorLevel: 'L' }),
          ]),
          h('p', { class: 'mb-2 mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500' }, '2FA Secret'),
          h(
            'div',
            {
              class: 'mb-4 rounded-xl border border-slate-200 bg-slate-50 p-2 text-center font-mono text-[16px] font-semibold tracking-[0.28em] text-slate-800 max-w-[300px]',
            },
            formattedSecret,
          ),
          h(
            'p',
            { class: 'mb-4 text-xs text-slate-600 max-w-[300px]' },
            'Write this secret down and keep it somewhere safe. You can use it to restore your authenticator app if you lose access to your device.',
          ),
          h(
            Checkbox,
            {
              defaultChecked: confirmedSecretSaved.value,
              onChange: (event: { target: { checked: boolean } }) => {
                confirmedSecretSaved.value = event.target.checked;
                modal.update({
                  okButtonProps: {
                    disabled: !confirmedSecretSaved.value,
                  },
                });
              },
            },
            {
              default: () => 'I have written down this secret.',
            },
          ),
          h('p', { class: 'my-2' }, 'Authentication Code'),
          h('p', { id: 'totp-code-error', class: 'hidden mb-2 text-sm text-red-500' }, 'Please enter a valid 6-digit code.'),
          h(Input, { placeholder: 'Code', id: 'totp-code', maxlength: 6, type: 'tel', pattern: '\\d{6}', class: 'max-w-[300px]' }),
        ]},
      ),
      okText: 'Submit',
      onOk: async () => {
        const code = document.querySelector<HTMLInputElement>('#totp-code')?.value || '';

        if (!code.trim()) {
          setModalError('totp-code-error', 'Please enter your MFA code.');
          throw new Error('User provided an empty MFA code');
        } else if (!/^\d{6}$/.test(code)) {
          console.log('Enrollment test error');
          setModalError('totp-code-error', 'Please enter a valid 6-digit code.');
          throw new Error('User provided an invalid MFA code');
        }


        const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, code);

        if (!requireName) {
          try {
            await multiFactor(currentUser).enroll(multiFactorAssertion, 'Authenticator App');
            resolve();
          } catch (error) {
            console.warn(error);
            Modal.error({ title: 'Error', content: 'Unable to enroll in MFA. Please try again.' });
            reject(new Error('Unable to enroll in MFA'));
          }
          return;
        }

        Modal.confirm({
          title: 'Enter Name',
          content: h(
            'div',
            {},
            { default: () => [
              h('p', {}, 'Please enter a name for this TOTP method.'),
              h(Input, { placeholder: 'Name', id: 'totp-name' }),
            ]},
          ),
          okText: 'Submit',
          onOk: async () => {
            const name = document.querySelector<HTMLInputElement>('#totp-name')?.value;
            try {
              await multiFactor(currentUser).enroll(multiFactorAssertion, name);
              resolve();
            } catch {
              Modal.error({ title: 'Error', content: 'Unable to enroll in MFA. Please try again.' });
              reject(new Error('Unable to enroll in MFA'));
            }
          },
          onCancel: () => reject(new Error('User cancelled the TOTP enrollment')),
        });
      },
      onCancel: () => reject(new Error('User cancelled the TOTP enrollment')),
    });
  });
}

async function reauthUserForCustomerMfa(): Promise<void> {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  if (!auth.currentUser) {
    throw new Error('no user');
  }

  const currentUser = auth.currentUser;
  const hasPasswordProvider = auth.currentUser.providerData.some((provider) => provider.providerId === 'password');
  if (!hasPasswordProvider) {
    await this.reauth();
    return;
  }

  await new Promise<void>((resolve, reject) => {
    Modal.confirm({
      title: 'Confirm Password',
      content: h(
        'div',
        {},
        { default: () => [
          h('p', {}, 'Please confirm your password to continue.'),
          h(Input, {
            placeholder: 'Password',
            type: 'password',
            autocomplete: 'current-password',
            id: 'customer-mfa-password',
          }),
        ]},
      ),
      okText: 'Continue',
      cancelText: 'Cancel',
      onOk: async () => {
        const password = document.querySelector<HTMLInputElement>('#customer-mfa-password')?.value || '';
        const email = getCurrentUserEmail.call(this);

        if (!password || !email) {
          Modal.error({
            title: 'Error',
            content: 'Password cannot be empty.',
          });
          reject(new Error('User provided an empty password'));
          return;
        }

        try {
          const credential = EmailAuthProvider.credential(email, password);
          const result = await reauthenticateWithCredential(currentUser, credential);
          this.user = result.user.toJSON();
          resolve();
        } catch (error: any) {
          if (error.code === 'auth/multi-factor-auth-required') {
            try {
              const result = await this.verifyMFA(error);
              this.user = result.user.toJSON();
              resolve();
            } catch (mfaError) {
              reject(mfaError);
            }
            return;
          }

          console.warn(error);
          Modal.error({
            title: 'Error',
            content: 'Incorrect password. Please try again.',
          });
          reject(new Error('User provided an incorrect password'));
        }
      },
      onCancel: () => reject(new Error('User cancelled the password reauthentication')),
    });
  });
}

export async function verifyMFA(
  data: MultiFactorError,
  router: Router | null = null,
  redirect: string | null = null,
): Promise<UserCredential> {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);
  const resolver = getMultiFactorResolver(auth, data);
  const smsSupported = canUseSmsMfaOnCurrentDomain();
  const hints = resolver.hints.filter((hint) => {
    return smsSupported || hint.factorId !== PhoneMultiFactorGenerator.FACTOR_ID;
  });

  if (hints.length === 0) {
    Modal.error({
      title: 'Error',
      content: 'No supported multi-factor methods are available on this domain. Please sign in on the main Rack Manage domain.',
    });
    return Promise.reject(new Error('no supported mfa methods'));
  }

  const hintsMap = hints.map((hint) => {
    return isPhoneMultiFactorInfo(hint)
      ? { value: hint.uid, label: hint.phoneNumber }
      : { value: hint.uid, label: hint.displayName };
  });

  const selectedHint = ref(hintsMap[0]?.value ?? '');
  const showMethodSelector = hints.length > 1;
  const showRecaptchaDisclaimer = hints.some((hint) => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID);

  return new Promise((mfaResolve, mfaReject) => {
    const promptForTotpCode = (multiFactorHint: MultiFactorInfo) => {
      Modal.confirm({
        title: 'Enter Code',
        content: h(
          'div',
          {},
          { default: () => [
            h('p', { class: 'mb-2' }, 'Please enter your MFA code.'),
            h(Input, { placeholder: 'Code', id: 'mfa-code', maxlength: 6, type: 'tel', pattern: '\\d{6}' }),
            h('p', {
              id: 'mfa-code-error',
              class: 'hidden mt-2 text-sm text-red-500',
            }),
          ]},
        ),
        okText: 'Submit',
        onOk: async () => {
          const code = document.querySelector<HTMLInputElement>('#mfa-code')?.value || '';
          clearModalError('mfa-code-error');

          if (!code.trim()) {
            setModalError('mfa-code-error', 'Please enter your MFA code.');
            throw new Error('User provided an empty MFA code');
          }

          const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(multiFactorHint.uid, code);
          try {
            const result = await resolver.resolveSignIn(multiFactorAssertion);
            if (router) { this.completeLogin(result, router, redirect); }
            mfaResolve(result);
          } catch {
            setModalError('mfa-code-error', 'Invalid code. Please try again.');
            throw new Error('User provided an invalid MFA code');
          }
        },
        onCancel: () => {
          clearRecaptchaVerifier();
          mfaReject(new Error('User cancelled the MFA verification'));
        },
      });
    };

    if (!showMethodSelector && hints[0] && hints[0].factorId === TotpMultiFactorGenerator.FACTOR_ID) {
      promptForTotpCode(hints[0]);
      return;
    }

    // Prompt user to choose MFA method from hints
    Modal.confirm({
      title: 'Multi-Factor Authentication',
      content: h(
        'div',
        { class: 'grid' },
        { default: () => [
          h('p', {}, {
            default: () => showMethodSelector
              ? 'Please select a multi-factor authentication method.'
              : 'Please complete multi-factor authentication to continue.',
          }),
          ...(showMethodSelector ? [h(Select,
            {
              style: { width: '100%' },
              placeholder: 'Select a method',
              onChange: (value) => { selectedHint.value = value?.toString() ?? hintsMap[0]?.value ?? ''; },
              defaultValue: selectedHint.value,
              options: hintsMap,
            },
          )] : []),
          ...(showRecaptchaDisclaimer ? [h('small', { class: 'mt-2' }, [
            'This site is protected by reCAPTCHA and the Google ',
            h('a', { href: 'https://policies.google.com/privacy', class: 'text-primary!' }, 'Privacy Policy'),
            ' and ',
            h('a', { href: 'https://policies.google.com/terms', class: 'text-primary!' }, 'Terms of Service'),
            ' apply.',
          ])] : []),
        ]},
      ),
      okText: 'Submit',
      cancelText: 'Cancel',
      onOk: async (): Promise<void> => {
        return new Promise((resolve) => {
          const multiFactorHint = hints.find((hint) => hint.uid === selectedHint.value);

          if (!multiFactorHint) {
            resolve();
            return mfaReject(new Error('Invalid MFA hint'));
          }

          if (multiFactorHint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
            const phoneInfoOptions = { multiFactorHint, session: resolver.session };
            const phoneAuthProvider = new PhoneAuthProvider(auth);
            phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, window.recaptchaVerifier)
              .then((verificationId) => {
                resolve();
                Modal.confirm({
                  title: 'Enter Code',
                  content: h(
                    'div',
                    {},
                    { default: () => [
                      h('p', { class: 'mb-2' }, 'Please enter your MFA code.'),
                      h(Input, { placeholder: 'Code', id: 'mfa-code', maxlength: 6, type: 'tel', pattern: '\\d{6}' }),
                      h('p', {
                        id: 'mfa-code-error',
                        class: 'hidden mt-2 text-sm text-red-500',
                      }),
                    ]},
                  ),
                  okText: 'Submit',
                  onOk: async () => {
                    const code = document.querySelector<HTMLInputElement>('#mfa-code')?.value || '';
                    clearModalError('mfa-code-error');
                    if (!code.trim()) {
                      setModalError('mfa-code-error', 'Please enter your MFA code.');
                      throw new Error('User provided an empty MFA code');
                    }

                    const cred = PhoneAuthProvider.credential(verificationId, code);
                    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
                    try {
                      const result = await resolver.resolveSignIn(multiFactorAssertion);
                      if (router) { this.completeLogin(result, router, redirect); }
                      mfaResolve(result);
                    } catch {
                      setModalError('mfa-code-error', 'Invalid code. Please try again.');
                      throw new Error('User provided an invalid MFA code');
                    }
                  },
                  onCancel: () => {
                    clearRecaptchaVerifier();
                    mfaReject(new Error('User cancelled the MFA verification'));
                  },
                });
              })
              .catch((error) => {
                clearRecaptchaVerifier();
                resolve();
                if (error.code === 'auth/invalid-app-credential') {
                  Modal.error({
                    title: 'Error',
                    content: 'SMS 2FA is not enabled for this domain.',
                  });
                } else {
                  Modal.error({
                    title: 'Error',
                    content: 'Failed to verify phone number. Please try again.',
                  });
                }
                mfaReject(new Error('Failed to verify phone number'));
              });
          } else {
            resolve();
            promptForTotpCode(multiFactorHint);
          }
        });
      },
      onCancel: () => {
        clearRecaptchaVerifier();
        mfaReject(new Error('User cancelled the MFA verification'));
      },
    });
  });
}

// Enroll a new MFA factor for the current user
// You must reauthenticate the user before completing this task
// Call store.reauth() to reauthenticate the user
export async function enrollMFA(): Promise<void> {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  return new Promise((resolve, reject) => {
    if (!auth.currentUser) {
      return reject(new Error('No user found'));
    }

    const multiFactorUser = multiFactor(auth.currentUser);
    const selectedHint = ref('Phone');

    // If user already has a TOTP factor, don't allow them to enroll again
    const totpDisabled = multiFactorUser.enrolledFactors.some(
      (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );

    Modal.confirm({
      title: 'Multi-Factor Authentication',
      content: h(
        'div',
        {},
        { default: () => [
          h('p', {}, 'Please select a multi-factor authentication method.'),
          h(
            Select,
            {
              style: { width: '100%' },
              placeholder: 'Select a method',
              onChange: (value) => { selectedHint.value = value as string; },
              defaultValue: selectedHint.value,
              options: [
                { value: 'Phone' },
                { value: 'TOTP', disabled: totpDisabled },
              ],
            },
          ),
        ]},
      ),
      okText: 'Submit',
      onOk: async () => {
        if (selectedHint.value === 'Phone') {
          Modal.confirm({
            title: 'Enter Phone Number',
            content: h(
              'div',
              { class: 'grid' },
              { default: () => [
                h('p', {}, 'Please enter your phone number.'),
                h(Input, { placeholder: 'Phone Number', id: 'phone-number', type: 'tel' }),
                h('small', { class: 'mt-2' }, [
                  'This site is protected by reCAPTCHA and the Google ',
                  h('a', { href: 'https://policies.google.com/privacy' }, 'Privacy Policy'),
                  ' and ',
                  h('a', { href: 'https://policies.google.com/terms' }, 'Terms of Service'),
                  ' apply.',
                ]),
              ]},
            ),
            okText: 'Submit',
            onOk: async () => {
              const multiFactorSession = await multiFactorUser.getSession();
              const phoneNumber = document.querySelector<HTMLInputElement>('#phone-number')?.value;
              const phoneOpts = { phoneNumber, session: multiFactorSession };
              const phoneAuthProvider = new PhoneAuthProvider(auth);

              phoneAuthProvider.verifyPhoneNumber(phoneOpts, window.recaptchaVerifier)
                .then((verificationId) => {
                  Modal.confirm({
                    title: 'Enter Code',
                    content: h(
                      'div',
                      {},
                      { default: () => [
                        h('p', { class: 'mb-2' }, {default: () => 'Please enter your MFA code.' }),
                        h(Input, { placeholder: 'Code', id: 'mfa-code', maxlength: 6, type: 'tel', pattern: '\\d{6}' }),
                      ]},
                    ),
                    okText: 'Submit',
                    onOk: async () => {
                      const code = document.querySelector<HTMLInputElement>('#mfa-code')?.value || '';
                      const cred = PhoneAuthProvider.credential(verificationId, code);
                      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
                      try {
                        if (!auth.currentUser) {
                          return reject(new Error('No user found'));
                        }
                        const result = await multiFactor(auth.currentUser).enroll(multiFactorAssertion);
                        resolve(result);
                      } catch {
                        Modal.error({ title: 'Error', content: 'Unable to enroll in MFA. Please try again.' });
                        reject(new Error('Unable to enroll in MFA'));
                      }
                    },
                    onCancel: () => reject(new Error('User cancelled the MFA enrollment')),
                  });
                });
            },
            onCancel: () => reject(new Error('User cancelled the MFA enrollment')),
          });
        } else {
          try {
            await enrollTotpFactor.call(this, { requireName: true });
            resolve();
          } catch (error) {
            reject(error);
          }
        }
      },
    });
  });
}

export async function manageUserTOTP({ router }: {router: Router}): Promise<void> {
  const auth = getAuth(getApp(this.firebaseApp));
  if (!auth.currentUser) {
    throw new Error('no user');
  }

  const multiFactorUser = multiFactor(auth.currentUser);
  const totpFactor = multiFactorUser.enrolledFactors.find(
    (factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  );

  await reauthUserForCustomerMfa.call(this);

  if (!totpFactor) {
    await enrollTotpFactor.call(this, { requireName: false });
    return;
  }

  await new Promise<void>((resolve, reject) => {
    Modal.confirm({
      title: 'Disable Two-Factor Authentication',
      content: 'Are you sure you want to remove your authenticator app? You will be logged out after disabling.',
      okText: 'Disable',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await multiFactorUser.unenroll(totpFactor.uid);
          resolve();
        } catch (error: any) {
          if (error.code === 'auth/user-token-expired') {
            resolve();
            return;
          }

          Modal.error({
            title: 'Error',
            content: 'Unable to remove your authenticator app.',
          });
          reject(error);
        }
      },
      onCancel: () => reject(new Error('User cancelled the TOTP removal')),
    });
  });

  message.success('Authenticator removed. Please login again.');
  await this.logout();
  router.push({
    path: '/auth/login',
    query: {
      to: router.currentRoute.value.fullPath,
    },
  });
}

export async function manageMFA({ router }: {router: Router}): Promise<void> {
  // Display all factors with a delete button next to it, and an add new factor button at the bottom
  return new Promise((resolve, reject) => {
    const auth = getAuth(getApp(this.firebaseApp));
    if (!auth.currentUser) {
      return reject(new Error('No user found'));
    }

    const multiFactorUser = multiFactor(auth.currentUser);
    const enrolledFactors = multiFactorUser.enrolledFactors;

    const enrolledFactorsMap = enrolledFactors.map((factor) => {
      return isPhoneMultiFactorInfo(factor)
        ? { value: factor.uid, label: factor.phoneNumber }
        : { value: factor.uid, label: factor.displayName };
    });

    const selectedFactor = ref(enrolledFactorsMap[0] ?? { value: '', label: '' });

    const modal = Modal.info({
      title: 'Manage Multi-Factor Authentication',
      content: h(
        'div',
        {},
        {
          default: () => [
            h(List, {},
              {
                default: () => enrolledFactorsMap.map((factor) => {
                  return h(List.Item, { key: factor.value }, {
                    default: () => [
                      h('span', {}, factor.label ?? ''),
                      h(Button, {
                        onClick: async () => {
                          selectedFactor.value = factor;
                          try {
                          // Enroll MFA is a sensitive action that requires recent authentication
                            modal.destroy();
                            resolve();
                            await this.reauth();
                            Modal.confirm({
                              title: 'Delete Factor',
                              content: h(
                                'p',
                                {},
                                { default: () => `Are you sure you want to delete ${
                                  enrolledFactorsMap.find(
                                    (factor) => factor.value === selectedFactor.value.value,
                                  )?.label || 'this factor'
                                }?`}),
                              okText: 'Delete',
                              onOk: async () => {
                                try {
                                  await multiFactorUser.unenroll(selectedFactor.value.value);
                                  message.success('Factor deleted. Please login again.');
                                  await this.logout();
                                  router.push({
                                    path: '/auth/login',
                                    query: {
                                      to: router.currentRoute.value.fullPath,
                                    },
                                  });
                                } catch (error: any) {
                                  // If this was the most recently enrolled factor, the user will be logged out
                                  if (error.code == 'auth/user-token-expired') {
                                    message.success('Factor deleted. Please login again.');
                                    await this.logout();
                                    // TEST THIS
                                    router.push({
                                      path: '/auth/login',
                                      query: {
                                        to: router.currentRoute.value.fullPath,
                                      },
                                    });
                                  } else {
                                    console.warn(error);
                                    message.error('Unable to delete factor.');
                                    reject(new Error('Unable to delete factor'));
                                  }
                                }
                              },
                            });
                          } catch (error) {
                            if (error instanceof Error && error.message === 'User cancelled the password reauthentication') {
                              message.error('Login Cancelled');
                            } else if (error instanceof Error && error.message === 'User provided an incorrect password') {
                              message.error('Incorrect Password');
                            } else {
                              console.warn(error);
                              message.error('An error occurred');
                            }
                          }
                        },
                      }, { default: () => h('i', { class: 'fas fa-trash' }) }),
                    ],
                  });
                }),
              },
            ),
            h('br'),
            enrolledFactors.length < 5 ?
              h(Button, { onClick: async () => {
                modal.destroy();
                resolve();

                try {
                // Enroll MFA is a sensitive action that requires recent authentication
                  await this.reauth();
                  await this.enrollMFA();
                  message.success('Factor added.');
                  // Re-open management modal
                  await this.manageMFA({ router });
                } catch (error) {
                  if (error instanceof Error && error.message === 'User cancelled the MFA enrollment') {
                    message.error('Login Cancelled');
                  } else if (error instanceof Error && error.message === 'User provided an incorrect password') {
                    message.error('Incorrect Password');
                  } else {
                    console.warn(error);
                    message.error('An error occurred');
                  }
                }
              } }, { default: () => 'Add New Factor' }) :
              h(Tooltip, { title: 'You have reached the maximum number of factors.' }, {
                default: () => {
                  return h(Button, { disabled: true }, { default: () => 'Add New Factor' });
                },
              }),
          ],
        },
      ),
      okText: 'Close',
    });
  });
}
